import React from 'react';
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Boxes,
  Check,
  Copy,
  Eye,
  KeyRound,
  Network,
  Play,
  Search,
  Sparkles,
  Wrench,
  Zap,
} from 'lucide-react';
import { CodeBlock } from './CodeBlock';
import { GatewayPlayground } from './GatewayPlayground';
import { slugForEndpoint } from './docsData';

// GatewayPage is the app.nz model-gateway hub: openpaths-parity documentation, a
// searchable model + provider catalogue, copy-paste code samples for every SDK,
// and a live in-browser playground. It mirrors ../openpaths (Docs + Models +
// Playground) folded into one page so /gateway is a real product surface, not a
// marketing stub.
//
// Prerender-safe: every browser-only value (origin, stored key, live catalogue)
// is initialised to a deterministic default and hydrated in an effect, so the
// server prerender and the first client render produce identical markup. The
// playground renders its empty initial state on the server prerender and
// hydrates cleanly (deterministic defaults; browser-only values set in effects).

const DEFAULT_ORIGIN = 'https://app.nz';

interface ProviderDetail {
  name: string;
  displayName: string;
  logo: string;
  website: string;
  enabled: boolean;
  modelCount: number;
}

interface CatalogModel {
  id: string;
  provider: string;
  aliases?: string[];
  fallback_models?: string[];
  input_price_per_1m?: number;
  output_price_per_1m?: number;
  context_window?: number;
  max_output_tokens?: number;
  supports_streaming?: boolean;
  supports_tools?: boolean;
  supports_vision?: boolean;
  modality?: string;
  supported_sizes?: string[];
  price_per_image?: number;
  price_per_video?: number;
  price_per_request?: number;
  price_per_minute?: number;
}

interface ModelsResponse {
  models: CatalogModel[];
  providers: string[];
  providerDetails: ProviderDetail[];
  total: number;
}

type Task = 'chat' | 'images' | 'embeddings' | 'video' | 'music' | '3d' | 'speech' | 'transcription';
type Lang = 'python' | 'javascript' | 'go' | 'curl';

const TASK_LABELS: Record<Task, string> = {
  chat: 'Chat',
  images: 'Images',
  embeddings: 'Embeddings',
  video: 'Video',
  music: 'Music',
  '3d': '3D',
  speech: 'Speech',
  transcription: 'Transcription',
};

const LANG_LABELS: Record<Lang, { label: string; hljs: string }> = {
  python: { label: 'Python', hljs: 'python' },
  javascript: { label: 'JavaScript', hljs: 'javascript' },
  go: { label: 'Go', hljs: 'go' },
  curl: { label: 'cURL', hljs: 'bash' },
};

const ENDPOINTS = [
  { method: 'POST', path: '/v1/chat/completions', description: 'OpenAI-compatible chat completions (streaming + tools).' },
  { method: 'POST', path: '/v1/messages', description: 'Anthropic-compatible Messages API for Claude + agent SDKs.' },
  { method: 'POST', path: '/v1/images/generations', description: 'Generate images (GPT Image, Flux, and more).' },
  { method: 'POST', path: '/v1/embeddings', description: 'Vector embeddings for search and RAG.' },
  { method: 'POST', path: '/v1/audio/speech', description: 'Text-to-speech (also /v1/tts).' },
  { method: 'POST', path: '/v1/audio/transcriptions', description: 'Speech-to-text transcription.' },
  { method: 'POST', path: '/v1/videos/generations', description: 'Generate video clips (job-based).' },
  { method: 'POST', path: '/v1/music/generations', description: 'Generate music (job-based).' },
  { method: 'POST', path: '/v1/audio/generations', description: 'Generate SFX and short audio (job-based).' },
  { method: 'POST', path: '/v1/3d/generations', description: 'Generate textured 3D models (job-based).' },
  { method: 'POST', path: '/v1/panoramas/generations', description: 'Expand one image into a 360 panorama or spatial world (job-based).' },
  { method: 'GET', path: '/api/gateway/models', description: 'List every routable model + provider metadata.' },
  { method: 'GET', path: '/api/gateway/resolve?model=', description: 'Preview how a model name routes (provider, fallbacks).' },
  { method: 'POST', path: '/api/gateway/route', description: 'Preview which model a bare "auto" request resolves to.' },
];

const AUTO_MODELS = [
  { id: 'auto', body: 'Default chat. Classifies the prompt and routes to a tier.' },
  { id: 'app/auto-code', body: 'Agents, refactors, and bug fixes.' },
  { id: 'app/auto-reasoning', body: 'Planning, math, and hard reasoning (auto thinking depth).' },
  { id: 'app/auto-cheap', body: 'Trivial and classification prompts on the cheapest model.' },
  { id: 'app/auto-vision', body: 'Prompts that include images.' },
  { id: 'app/auto-image', body: 'Image generation with a hosted fallback.' },
];

function buildSamples(origin: string, key: string): Record<Task, { description: string } & Record<Lang, string>> {
  const v1 = `${origin}/v1`;
  return {
    chat: {
      description: 'Send messages, stream tokens back. OpenAI SDK drop-in: change base_url and key.',
      python: `from openai import OpenAI

client = OpenAI(base_url="${v1}", api_key="${key}")

resp = client.chat.completions.create(
    model="auto",  # smart routing, or pin a model like "gpt-latest"
    messages=[{"role": "user", "content": "Write a tiny SSE server in Go."}],
    reasoning_effort="auto",  # none | low | medium | high | xhigh | auto
)
print(resp.choices[0].message.content)`,
      javascript: `import OpenAI from "openai";

const client = new OpenAI({ baseURL: "${v1}", apiKey: "${key}" });

const resp = await client.chat.completions.create({
  model: "auto",
  messages: [{ role: "user", content: "Write a tiny SSE server in Go." }],
  reasoning_effort: "auto",
});
console.log(resp.choices[0].message.content);`,
      go: `package main

import (
	"bytes"
	"fmt"
	"io"
	"net/http"
)

func main() {
	body := []byte(\`{"model":"auto","messages":[{"role":"user","content":"Write a tiny SSE server in Go."}]}\`)
	req, _ := http.NewRequest("POST", "${v1}/chat/completions", bytes.NewReader(body))
	req.Header.Set("Content-Type", "application/json")
	req.Header.Set("Authorization", "Bearer ${key}")
	resp, _ := http.DefaultClient.Do(req)
	defer resp.Body.Close()
	out, _ := io.ReadAll(resp.Body)
	fmt.Println(string(out))
}`,
      curl: `curl ${v1}/chat/completions \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${key}" \\
  -d '{
    "model": "auto",
    "messages": [{"role": "user", "content": "Write a tiny SSE server in Go."}]
  }'`,
    },
    images: {
      description: 'Generate images. The model field picks the provider; "auto" routes.',
      python: `from openai import OpenAI
import base64, pathlib

client = OpenAI(base_url="${v1}", api_key="${key}")

result = client.images.generate(
    model="auto",
    prompt="A beige coffee mug on a wooden table, natural light",
    size="1024x1024",
    n=1,
)
pathlib.Path("mug.png").write_bytes(base64.b64decode(result.data[0].b64_json))`,
      javascript: `import OpenAI from "openai";
import { writeFile } from "node:fs/promises";

const client = new OpenAI({ baseURL: "${v1}", apiKey: "${key}" });

const result = await client.images.generate({
  model: "auto",
  prompt: "A beige coffee mug on a wooden table, natural light",
  size: "1024x1024",
  n: 1,
});
await writeFile("mug.png", Buffer.from(result.data[0].b64_json, "base64"));`,
      go: `package main

import (
	"bytes"
	"fmt"
	"io"
	"net/http"
)

func main() {
	body := []byte(\`{"model":"auto","prompt":"A beige coffee mug on a wooden table","size":"1024x1024","n":1}\`)
	req, _ := http.NewRequest("POST", "${v1}/images/generations", bytes.NewReader(body))
	req.Header.Set("Content-Type", "application/json")
	req.Header.Set("Authorization", "Bearer ${key}")
	resp, _ := http.DefaultClient.Do(req)
	defer resp.Body.Close()
	out, _ := io.ReadAll(resp.Body)
	fmt.Println(string(out))
}`,
      curl: `curl ${v1}/images/generations \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${key}" \\
  -d '{"model": "auto", "prompt": "A beige coffee mug on a wooden table", "size": "1024x1024", "n": 1}'`,
    },
    embeddings: {
      description: 'Turn text into vectors for search, clustering, and RAG.',
      python: `from openai import OpenAI

client = OpenAI(base_url="${v1}", api_key="${key}")

resp = client.embeddings.create(
    model="text-embedding-3-small",
    input=["app.nz routes every model through one API"],
)
print(len(resp.data[0].embedding))`,
      javascript: `import OpenAI from "openai";

const client = new OpenAI({ baseURL: "${v1}", apiKey: "${key}" });

const resp = await client.embeddings.create({
  model: "text-embedding-3-small",
  input: ["app.nz routes every model through one API"],
});
console.log(resp.data[0].embedding.length);`,
      go: `package main

import (
	"bytes"
	"fmt"
	"io"
	"net/http"
)

func main() {
	body := []byte(\`{"model":"text-embedding-3-small","input":["app.nz routes every model through one API"]}\`)
	req, _ := http.NewRequest("POST", "${v1}/embeddings", bytes.NewReader(body))
	req.Header.Set("Content-Type", "application/json")
	req.Header.Set("Authorization", "Bearer ${key}")
	resp, _ := http.DefaultClient.Do(req)
	defer resp.Body.Close()
	out, _ := io.ReadAll(resp.Body)
	fmt.Println(string(out))
}`,
      curl: `curl ${v1}/embeddings \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${key}" \\
  -d '{"model": "text-embedding-3-small", "input": ["one API for every model"]}'`,
    },
    video: {
      description: 'Generate video clips. Job-based: the call returns a job to poll.',
      python: `from openai import OpenAI

client = OpenAI(base_url="${v1}", api_key="${key}")

job = client.post("/videos/generations", body={
    "model": "auto",
    "prompt": "A cinematic fly-through of a neon-lit cyberpunk street at night.",
    "resolution": "1280x720",
}, cast_to=dict)

while job["status"] not in ("completed", "failed"):
    job = client.get(f"/videos/generations/{job['id']}", cast_to=dict)
print(job["result"]["video_url"])`,
      javascript: `const res = await fetch("${v1}/videos/generations", {
  method: "POST",
  headers: { "Content-Type": "application/json", Authorization: "Bearer ${key}" },
  body: JSON.stringify({
    model: "auto",
    prompt: "A cinematic fly-through of a neon-lit cyberpunk street at night.",
    resolution: "1280x720",
  }),
});
let job = await res.json();
while (job.status && job.status !== "completed" && job.status !== "failed") {
  await new Promise((r) => setTimeout(r, 2000));
  job = await (await fetch("${v1}/videos/generations/" + job.id, {
    headers: { Authorization: "Bearer ${key}" },
  })).json();
}
console.log((job.result ?? job).video_url);`,
      go: `package main

import (
	"bytes"
	"fmt"
	"io"
	"net/http"
)

func main() {
	body := []byte(\`{"model":"auto","prompt":"A neon-lit cyberpunk street at night.","resolution":"1280x720"}\`)
	req, _ := http.NewRequest("POST", "${v1}/videos/generations", bytes.NewReader(body))
	req.Header.Set("Content-Type", "application/json")
	req.Header.Set("Authorization", "Bearer ${key}")
	resp, _ := http.DefaultClient.Do(req)
	defer resp.Body.Close()
	out, _ := io.ReadAll(resp.Body)
	fmt.Println(string(out)) // poll result.id for the finished video_url
}`,
      curl: `curl ${v1}/videos/generations \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${key}" \\
  -d '{"model": "auto", "prompt": "A neon-lit cyberpunk street at night.", "resolution": "1280x720"}'`,
    },
    music: {
      description: 'Generate music or SFX. Music uses /music/generations; SFX uses /audio/generations.',
      python: `from openai import OpenAI

client = OpenAI(base_url="${v1}", api_key="${key}")

job = client.post("/music/generations", body={
    "model": "music-2.5",
    "prompt": "Warm synthwave theme with analog drums and a hopeful chorus",
    "duration": 30,
}, cast_to=dict)

print(job.get("audio", {}).get("url") or job.get("audio_url") or job)`,
      javascript: `const res = await fetch("${v1}/music/generations", {
  method: "POST",
  headers: { "Content-Type": "application/json", Authorization: "Bearer ${key}" },
  body: JSON.stringify({
    model: "music-2.5",
    prompt: "Warm synthwave theme with analog drums and a hopeful chorus",
    duration: 30,
  }),
});
const data = await res.json();
console.log(data.audio?.url || data.audio_url || data);`,
      go: `package main

import (
	"bytes"
	"fmt"
	"io"
	"net/http"
)

func main() {
	body := []byte(\`{"model":"music-2.5","prompt":"Warm synthwave theme with analog drums","duration":30}\`)
	req, _ := http.NewRequest("POST", "${v1}/music/generations", bytes.NewReader(body))
	req.Header.Set("Content-Type", "application/json")
	req.Header.Set("Authorization", "Bearer ${key}")
	resp, _ := http.DefaultClient.Do(req)
	defer resp.Body.Close()
	out, _ := io.ReadAll(resp.Body)
	fmt.Println(string(out))
}`,
      curl: `curl ${v1}/music/generations \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${key}" \\
  -d '{"model": "music-2.5", "prompt": "Warm synthwave theme with analog drums", "duration": 30}'`,
    },
    '3d': {
      description: 'Convert a reference image URL into a textured GLB model.',
      python: `from openai import OpenAI

client = OpenAI(base_url="${v1}", api_key="${key}")

result = client.post("/3d/generations", body={
    "model": "pixal3d-image-to-3d",
    "image_url": "https://example.com/reference.png",
    "texture_size": 1024,
    "remesh": True,
}, cast_to=dict)

print(result["model_glb"]["url"])`,
      javascript: `const res = await fetch("${v1}/3d/generations", {
  method: "POST",
  headers: { "Content-Type": "application/json", Authorization: "Bearer ${key}" },
  body: JSON.stringify({
    model: "pixal3d-image-to-3d",
    image_url: "https://example.com/reference.png",
    texture_size: 1024,
    remesh: true,
  }),
});
const data = await res.json();
console.log(data.model_glb.url);`,
      go: `package main

import (
	"bytes"
	"fmt"
	"io"
	"net/http"
)

func main() {
	body := []byte(\`{"model":"pixal3d-image-to-3d","image_url":"https://example.com/reference.png","texture_size":1024,"remesh":true}\`)
	req, _ := http.NewRequest("POST", "${v1}/3d/generations", bytes.NewReader(body))
	req.Header.Set("Content-Type", "application/json")
	req.Header.Set("Authorization", "Bearer ${key}")
	resp, _ := http.DefaultClient.Do(req)
	defer resp.Body.Close()
	out, _ := io.ReadAll(resp.Body)
	fmt.Println(string(out))
}`,
      curl: `curl ${v1}/3d/generations \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${key}" \\
  -d '{"model": "pixal3d-image-to-3d", "image_url": "https://example.com/reference.png", "texture_size": 1024, "remesh": true}'`,
    },
    speech: {
      description: 'Text-to-speech. Returns audio bytes to stream or save.',
      python: `from openai import OpenAI

client = OpenAI(base_url="${v1}", api_key="${key}")

with client.audio.speech.with_streaming_response.create(
    model="xai-tts",
    voice="eve",
    input="Welcome to app.nz. One API key routes every model.",
) as resp:
    resp.stream_to_file("welcome.mp3")`,
      javascript: `const res = await fetch("${v1}/audio/speech", {
  method: "POST",
  headers: { "Content-Type": "application/json", Authorization: "Bearer ${key}" },
  body: JSON.stringify({ model: "xai-tts", voice: "eve", input: "Welcome to app.nz." }),
});
const buf = Buffer.from(await res.arrayBuffer());
await (await import("node:fs/promises")).writeFile("welcome.mp3", buf);`,
      go: `package main

import (
	"bytes"
	"io"
	"net/http"
	"os"
)

func main() {
	body := []byte(\`{"model":"xai-tts","voice":"eve","input":"Welcome to app.nz."}\`)
	req, _ := http.NewRequest("POST", "${v1}/audio/speech", bytes.NewReader(body))
	req.Header.Set("Content-Type", "application/json")
	req.Header.Set("Authorization", "Bearer ${key}")
	resp, _ := http.DefaultClient.Do(req)
	defer resp.Body.Close()
	out, _ := os.Create("welcome.mp3")
	defer out.Close()
	io.Copy(out, resp.Body)
}`,
      curl: `curl ${v1}/audio/speech \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${key}" \\
  -d '{"model": "xai-tts", "voice": "eve", "input": "Welcome to app.nz."}' \\
  --output welcome.mp3`,
    },
    transcription: {
      description: 'Upload an audio file, get a transcript back.',
      python: `from openai import OpenAI

client = OpenAI(base_url="${v1}", api_key="${key}")

with open("meeting.mp3", "rb") as f:
    transcript = client.audio.transcriptions.create(model="gpt-4o-transcribe", file=f)
print(transcript.text)`,
      javascript: `import OpenAI from "openai";
import fs from "node:fs";

const client = new OpenAI({ baseURL: "${v1}", apiKey: "${key}" });

const transcript = await client.audio.transcriptions.create({
  model: "gpt-4o-transcribe",
  file: fs.createReadStream("meeting.mp3"),
});
console.log(transcript.text);`,
      go: `package main

import (
	"bytes"
	"fmt"
	"io"
	"mime/multipart"
	"net/http"
	"os"
)

func main() {
	var body bytes.Buffer
	w := multipart.NewWriter(&body)
	w.WriteField("model", "gpt-4o-transcribe")
	f, _ := os.Open("meeting.mp3")
	defer f.Close()
	part, _ := w.CreateFormFile("file", "meeting.mp3")
	io.Copy(part, f)
	w.Close()
	req, _ := http.NewRequest("POST", "${v1}/audio/transcriptions", &body)
	req.Header.Set("Content-Type", w.FormDataContentType())
	req.Header.Set("Authorization", "Bearer ${key}")
	resp, _ := http.DefaultClient.Do(req)
	defer resp.Body.Close()
	out, _ := io.ReadAll(resp.Body)
	fmt.Println(string(out))
}`,
      curl: `curl ${v1}/audio/transcriptions \\
  -H "Authorization: Bearer ${key}" \\
  -F model=gpt-4o-transcribe \\
  -F file=@meeting.mp3`,
    },
  };
}

function fmtPrice(p?: number): string {
  if (!p || p <= 0) return '—';
  if (p < 1) return `$${p.toFixed(2)}`;
  return `$${p.toFixed(2)}`;
}

function fmtContext(n?: number): string {
  if (!n || n <= 0) return '—';
  if (n >= 1000) return `${Math.round(n / 1000)}K`;
  return String(n);
}

export const GatewayPage: React.FC = () => {
  const [origin, setOrigin] = React.useState(DEFAULT_ORIGIN);
  const [exampleKey, setExampleKey] = React.useState('pk_live_...');
  const [task, setTask] = React.useState<Task>('chat');
  const [lang, setLang] = React.useState<Lang>('python');
  const [copied, setCopied] = React.useState(false);

  const [data, setData] = React.useState<ModelsResponse | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [query, setQuery] = React.useState('');
  const [providerFilter, setProviderFilter] = React.useState('');

  // Deep-link tab for the playground: #playground scrolls to it, and
  // #playground-image / #playground-video / … opens that surface directly (the
  // "art generation playground" links land on the image tab, not chat).
  const [playgroundTab, setPlaygroundTab] = React.useState<string | undefined>(undefined);

  // Hydrate browser-only values after mount so the prerender markup matches.
  React.useEffect(() => {
    setOrigin(window.location.origin);
    try {
      const stored = localStorage.getItem('appnz_gateway_key');
      if (stored) setExampleKey(stored);
    } catch {
      /* storage blocked */
    }
    const applyPlaygroundHash = () => {
      const m = window.location.hash.match(/^#playground(?:[-=]([a-z0-9]+))?$/i);
      if (!m) return;
      const t = (m[1] || '').toLowerCase();
      if (['chat', 'image', 'embeddings', 'video', 'music', 'speech', 'transcription', '3d'].includes(t)) {
        setPlaygroundTab(t);
      }
      window.setTimeout(
        () => document.getElementById('playground')?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
        120,
      );
    };
    applyPlaygroundHash();
    window.addEventListener('hashchange', applyPlaygroundHash);
    return () => window.removeEventListener('hashchange', applyPlaygroundHash);
  }, []);

  React.useEffect(() => {
    let aborted = false;
    fetch('/api/gateway/models')
      .then((r) => {
        if (!r.ok) throw new Error(`models ${r.status}`);
        return r.json();
      })
      .then((d: ModelsResponse) => {
        if (!aborted) setData(d);
      })
      .catch((e) => {
        if (!aborted) setLoadError(String(e?.message || e));
      });
    return () => {
      aborted = true;
    };
  }, []);

  const samples = React.useMemo(() => buildSamples(origin, exampleKey), [origin, exampleKey]);
  const active = samples[task];

  const copyCurl = async () => {
    try {
      await navigator.clipboard.writeText(active.curl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked */
    }
  };

  const providers = data?.providerDetails || [];
  const models = data?.models || [];
  const filteredModels = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    return models.filter((m) => {
      if (providerFilter && m.provider.toLowerCase() !== providerFilter.toLowerCase()) return false;
      if (!q) return true;
      return (
        m.id.toLowerCase().includes(q) ||
        m.provider.toLowerCase().includes(q) ||
        (m.aliases || []).some((a) => a.toLowerCase().includes(q))
      );
    });
  }, [models, query, providerFilter]);

  return (
    <main className="min-h-screen bg-black px-4 pb-16 pt-24 text-white sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1800px]">
        <a href="/" className="mb-5 inline-flex items-center gap-2 text-sm font-semibold text-white/48 hover:text-white">
          <ArrowLeft size={16} /> Back to overview
        </a>

        {/* Hero */}
        <div className="mb-8 grid gap-5 lg:grid-cols-[0.95fr_1.05fr] lg:items-end">
          <div>
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-white/54">
              <Network size={14} /> model gateway
            </div>
            <h1 className="font-display text-2xl font-bold leading-tight tracking-tight sm:text-4xl">
              One OpenAI-compatible API for every model and provider.
            </h1>
          </div>
          <div>
            <p className="text-sm leading-7 text-white/58">
              Route chat, images, embeddings, audio, and video through app.nz. Point any OpenAI or Anthropic SDK
              at the base URL below, keep your code, and switch models with one field. <code className="rounded bg-white/10 px-1 text-white/80">auto</code> routing,
              adaptive thinking, prompt caching, automatic failover, usage metering, and one API key.
            </p>
            <div className="mt-4 flex flex-col gap-3 sm:flex-row">
              <a
                href="/signup"
                className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-sky-400 px-4 text-sm font-bold text-black transition-colors hover:bg-sky-300"
              >
                Create a key <ArrowRight size={17} />
              </a>
              <a
                href="#playground"
                className="inline-flex h-11 items-center justify-center gap-2 rounded-lg border border-white/12 bg-white/[0.04] px-4 text-sm font-semibold text-white transition-colors hover:bg-white hover:text-black"
              >
                <Play size={16} /> Open playground
              </a>
              <a
                href="#models"
                className="inline-flex h-11 items-center justify-center gap-2 rounded-lg border border-white/12 bg-white/[0.04] px-4 text-sm font-semibold text-white/80 transition-colors hover:text-white"
              >
                Browse models
              </a>
              <a
                href="/spaces"
                className="inline-flex h-11 items-center justify-center gap-2 rounded-lg border border-white/12 bg-white/[0.04] px-4 text-sm font-semibold text-white/80 transition-colors hover:text-white"
              >
                Model spaces
              </a>
            </div>
          </div>
        </div>

        {/* Quickstart + reference */}
        <div className="grid gap-4 lg:grid-cols-[1.25fr_0.75fr] lg:items-start">
          {/* Code card */}
          <section className="min-w-0 rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="gateway-code-card">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-xs font-mono text-white/40">Base URL</div>
                <code className="text-sm text-white/85" data-testid="gateway-base-url">{`${origin}/v1`}</code>
              </div>
              <button
                onClick={copyCurl}
                data-testid="gateway-copy-curl"
                className="inline-flex items-center gap-2 rounded-lg border border-white/10 px-3 py-2 text-xs font-mono text-white/70 transition-colors hover:border-white/20 hover:text-white"
              >
                {copied ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
                {copied ? 'Copied' : 'Copy cURL'}
              </button>
            </div>

            <div className="mb-3 flex flex-wrap gap-1 border-b border-white/10">
              {(Object.keys(TASK_LABELS) as Task[]).map((t) => (
                <button
                  key={t}
                  onClick={() => setTask(t)}
                  data-testid={`gateway-task-${t}`}
                  className={`-mb-px border-b-2 px-3 py-2 text-xs font-mono transition-colors ${
                    task === t ? 'border-white text-white' : 'border-transparent text-white/40 hover:text-white/70'
                  }`}
                >
                  {TASK_LABELS[t]}
                </button>
              ))}
            </div>

            <div className="mb-3 flex flex-wrap gap-1.5">
              {(Object.keys(LANG_LABELS) as Lang[]).map((l) => (
                <button
                  key={l}
                  onClick={() => setLang(l)}
                  data-testid={`gateway-lang-${l}`}
                  className={`rounded-md px-2.5 py-1 text-xs font-mono transition-colors ${
                    lang === l ? 'bg-sky-400/15 text-sky-200' : 'bg-white/[0.04] text-white/55 hover:text-white'
                  }`}
                >
                  {LANG_LABELS[l].label}
                </button>
              ))}
            </div>

            <div className="mb-2 text-xs font-mono text-white/40">{active.description}</div>
            <CodeBlock code={active[lang]} language={LANG_LABELS[lang].hljs} testId="gateway-code-sample" />
          </section>

          {/* Reference column */}
          <section className="grid min-w-0 gap-4">
            <div className="min-w-0 rounded-2xl border border-white/10 bg-white/[0.02] p-5">
              <div className="mb-3 flex items-start gap-2">
                <KeyRound size={18} className="mt-0.5 text-white/50" />
                <div className="min-w-0">
                  <h2 className="text-lg font-bold tracking-tight">Authentication</h2>
                  <p className="text-sm text-white/58">
                    Send your key as <code className="rounded bg-white/10 px-1 text-white/80">Authorization: Bearer pk_live_...</code>.
                    Create one on the <a href="/account" className="text-white underline underline-offset-4">account page</a>.
                    Signed in here, same-origin calls use your session automatically.
                  </p>
                </div>
              </div>
            </div>

            <div className="min-w-0 rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="gateway-anthropic-setup">
              <div className="mb-2 flex items-center gap-2">
                <Network size={16} className="text-white/50" />
                <h2 className="text-lg font-bold tracking-tight">Claude Code + Anthropic SDK</h2>
              </div>
              <p className="mb-3 text-sm leading-6 text-white/55">
                Use the site root, no <code className="rounded bg-white/10 px-1 text-white/80">/v1</code>. The gateway translates Messages,
                tools, and streaming for every compatible provider model.
              </p>
              <CodeBlock
                code={`$env:ANTHROPIC_BASE_URL = "${origin}"
$env:ANTHROPIC_AUTH_TOKEN = $env:APP_API_KEY
$env:ANTHROPIC_API_KEY = ""
$env:ANTHROPIC_MODEL = "nvidia/deepseek-v4-pro"
claude`}
                language="powershell"
                testId="gateway-anthropic-code"
              />
            </div>

            <div className="min-w-0 rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="gateway-auto-models">
              <div className="mb-1 flex items-center gap-2">
                <Zap size={16} className="text-white/50" />
                <h2 className="text-lg font-bold tracking-tight">Auto routing</h2>
              </div>
              <p className="mb-3 text-sm text-white/55">
                Send <code className="rounded bg-white/10 px-1 text-white/80">"model": "auto"</code> and the gateway
                classifies the prompt, then routes to a tier and thinking depth. Or pin a model id. Stable, long
                Anthropic system/tool prefixes are cached automatically; explicit cache controls are preserved.
              </p>
              <div className="space-y-1.5 text-sm">
                {AUTO_MODELS.map((m) => (
                  <div key={m.id} className="flex flex-col">
                    <code className="font-mono text-[13px] text-white/85">{m.id}</code>
                    <span className="text-xs text-white/45">{m.body}</span>
                  </div>
                ))}
              </div>
            </div>

          </section>
        </div>

        {/* Endpoints — full-width grid so the reference column stays level with
            the code card instead of leaving a tall empty gutter. */}
        <section className="mt-4 rounded-2xl border border-white/10 bg-white/[0.02] p-5">
          <h2 className="mb-3 flex items-center gap-2 text-lg font-bold tracking-tight">
            <BookOpen size={16} className="text-white/50" /> Endpoints
          </h2>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3" data-testid="gateway-endpoints">
            {ENDPOINTS.map((e) => {
              const slug = slugForEndpoint(e.method, e.path);
              const href = slug ? `/docs/${slug}` : '/docs/models';
              return (
                <a
                  key={`${e.method} ${e.path}`}
                  href={href}
                  data-testid="gateway-endpoint-link"
                  className="group block rounded-lg border border-white/10 p-3 transition-colors hover:border-white/25 hover:bg-white/[0.04]"
                >
                  <div className="flex items-center gap-2">
                    <span className="rounded bg-white/10 px-2 py-0.5 font-mono text-[10px] text-white/80">{e.method}</span>
                    <code className="min-w-0 truncate text-[13px] text-white/90">{e.path}</code>
                    <ArrowRight size={13} className="ml-auto shrink-0 text-white/25 transition-colors group-hover:text-white/70" />
                  </div>
                  <p className="mt-1 text-xs text-white/50">{e.description}</p>
                </a>
              );
            })}
          </div>
        </section>

        {/* Fusion — panel inference with a judge that regenerates the best answer. */}
        <section id="fusion" className="mt-10 scroll-mt-24">
          <div className="mb-3 flex items-center gap-2">
            <Sparkles size={18} className="text-amber-200" />
            <h2 className="font-display text-2xl font-bold tracking-tight">Fusion</h2>
          </div>
          <p className="mb-4 max-w-3xl text-sm text-white/55">
            Fusion sends one prompt to a panel of models in parallel; a judge model then regenerates the strongest
            answer from every candidate. Any model id works, including custom OpenRouter slugs like{' '}
            <code className="rounded bg-white/10 px-1 font-mono text-xs">openrouter/vendor/model</code>. Try it on the{' '}
            <a href="/fusion" className="underline hover:text-white">Fusion page</a>.
          </p>
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="min-w-0 rounded-xl border border-white/10 bg-white/[0.02] p-4">
              <div className="mb-2 text-sm font-bold">One-shot fusion</div>
              <CodeBlock
                language="bash"
                code={`curl -X POST ${origin}/api/tools/fusion \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "prompt": "Explain vector databases in two sentences",
    "models": ["app/auto-fast", "anthropic/claude-haiku-4-5", "openrouter/qwen/qwen3.7-plus"],
    "judgeModel": "app/auto"
  }'`}
                testId="gateway-fusion-curl"
              />
              <p className="mt-2 text-xs text-white/45">
                Returns <code className="font-mono">{'{ results: [{model, output, ms, costMicros}], fused, judgeModel }'}</code>.
              </p>
            </div>
            <div className="min-w-0 rounded-xl border border-white/10 bg-white/[0.02] p-4">
              <div className="mb-2 text-sm font-bold">Saved fusion space API</div>
              <CodeBlock
                language="bash"
                code={`# Save a reusable panel once:
curl -X POST ${origin}/api/tools/fusion/spaces \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"name": "my-panel", "models": ["app/auto-fast", "gemini/gemini-2.5-flash"], "judgeModel": "app/auto"}'

# Then run it forever after at your own endpoint:
curl -X POST ${origin}/api/fusion/SPACE_ID/run \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"prompt": "your prompt here"}'`}
                testId="gateway-fusion-space-curl"
              />
              <p className="mt-2 text-xs text-white/45">
                Spaces: <code className="font-mono">GET/POST /api/tools/fusion/spaces</code>,{' '}
                <code className="font-mono">DELETE /api/tools/fusion/spaces/&#123;id&#125;</code>. Per-call{' '}
                <code className="font-mono">models</code> overrides the saved panel.
              </p>
            </div>
          </div>
        </section>

        {/* Playground */}
        <section id="playground" className="mt-10 scroll-mt-24">
          <div className="mb-3 flex items-center gap-2">
            <Play size={18} className="text-white/55" />
            <h2 className="font-display text-2xl font-bold tracking-tight">Playground</h2>
          </div>
          <p className="mb-4 max-w-3xl text-sm text-white/55">
            Run real requests against any model here: chat, image, embeddings, video, music, speech,
            transcription, and image→3D, each with a live preview and the raw JSON response. Signed in, it uses your
            session; otherwise paste an API key.
          </p>
          <GatewayPlayground
            key={playgroundTab || 'default'}
            models={models}
            origin={origin}
            initialTab={playgroundTab as never}
          />
        </section>

        {/* Models browser */}
        <section id="models" className="mt-10 scroll-mt-24">
          <div className="mb-3 flex items-center gap-2">
            <Boxes size={18} className="text-white/55" />
            <h2 className="font-display text-2xl font-bold tracking-tight">
              Models {data ? <span className="text-white/40">({data.total})</span> : null}
            </h2>
            <a href="/models" className="ml-auto inline-flex items-center gap-1 text-xs font-mono text-white/45 hover:text-white">
              Full directory <ArrowRight size={13} />
            </a>
          </div>

          <div className="mb-4 flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[220px]">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/35" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search models, aliases, providers…"
                data-testid="gateway-model-search"
                className="h-10 w-full rounded-lg border border-white/10 bg-white/[0.03] pl-9 pr-3 text-sm text-white placeholder:text-white/35 focus:border-white/25 focus:outline-none"
              />
            </div>
            <select
              value={providerFilter}
              onChange={(e) => setProviderFilter(e.target.value)}
              data-testid="gateway-provider-filter"
              className="h-10 rounded-lg border border-white/10 bg-white/[0.03] px-3 text-sm text-white focus:border-white/25 focus:outline-none"
            >
              <option value="">All providers</option>
              {providers.map((p) => (
                <option key={p.name} value={p.name}>
                  {p.displayName} ({p.modelCount})
                </option>
              ))}
            </select>
          </div>

          {loadError && (
            <div className="rounded-lg border border-red-500/20 bg-red-500/5 p-4 text-sm text-red-300" data-testid="gateway-models-error">
              Could not load the model catalogue: {loadError}
            </div>
          )}

          {!data && !loadError && (
            <div className="rounded-lg border border-white/10 bg-white/[0.02] p-8 text-center text-sm text-white/40">
              Loading catalogue…
            </div>
          )}

          {data && (
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5" data-testid="gateway-model-grid">
              {filteredModels.slice(0, 240).map((m) => (
                <a key={`${m.provider}:${m.id}`} href={`/models/${encodeURIComponent(m.id)}`} data-testid="gateway-model-card" className="group block rounded-xl border border-white/10 bg-white/[0.025] p-3 transition-colors hover:border-white/25 hover:bg-white/[0.04]">
                  <div className="flex items-start justify-between gap-2">
                    <code className="break-all text-sm font-semibold text-white/90">{m.id}</code>
                    <ArrowRight size={14} className="mt-0.5 shrink-0 text-white/20 transition-colors group-hover:text-white/60" />
                  </div>
                  <div className="mt-1 text-xs text-white/45">{m.provider}</div>
                  <div className="mt-2 flex flex-wrap gap-1.5 text-[10px] font-mono">
                    <span className="rounded bg-white/[0.06] px-1.5 py-0.5 text-white/55">ctx {fmtContext(m.context_window)}</span>
                    {(m.input_price_per_1m || m.output_price_per_1m) ? (
                      <span className="rounded bg-white/[0.06] px-1.5 py-0.5 text-white/55">
                        {fmtPrice(m.input_price_per_1m)}/{fmtPrice(m.output_price_per_1m)} per 1M
                      </span>
                    ) : null}
                    {m.supports_vision && (
                      <span className="inline-flex items-center gap-0.5 rounded bg-white/[0.06] px-1.5 py-0.5 text-white/55"><Eye size={10} /> vision</span>
                    )}
                    {m.supports_tools && (
                      <span className="inline-flex items-center gap-0.5 rounded bg-white/[0.06] px-1.5 py-0.5 text-white/55"><Wrench size={10} /> tools</span>
                    )}
                  </div>
                </a>
              ))}
              {filteredModels.length === 0 && (
                <div className="col-span-full rounded-lg border border-white/10 bg-white/[0.02] p-6 text-center text-sm text-white/40">
                  No models match “{query}”.
                </div>
              )}
            </div>
          )}
        </section>

        {/* Providers */}
        <section id="providers" className="mt-10 scroll-mt-24">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <Network size={18} className="text-white/55" />
            <h2 className="font-display text-2xl font-bold tracking-tight">
              Providers {data ? <span className="text-white/40">({providers.length})</span> : null}
            </h2>
            <a href="/providers" className="ml-auto text-sm font-semibold text-sky-300 hover:text-sky-200">
              Become a provider →
            </a>
          </div>
          {data && (
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4" data-testid="gateway-provider-grid">
              {providers.map((p) => (
                <button
                  key={p.name}
                  onClick={() => {
                    setProviderFilter(p.name);
                    document.getElementById('models')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  data-testid="gateway-provider-card"
                  className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.025] p-3 text-left transition-colors hover:border-white/25"
                >
                  <img
                    src={p.logo}
                    alt=""
                    loading="lazy"
                    className="h-8 w-8 shrink-0 rounded-md border border-white/10 bg-white/[0.04] object-contain p-1"
                  />
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-white/90">{p.displayName}</div>
                    <div className="text-xs text-white/40">{p.modelCount} models</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>
      </div>
    </main>
  );
};
